import { debounce } from 'lodash-es';
import { AlertCircle } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { type Country, isValidPhoneNumber } from 'react-phone-number-input';

import { PhoneInput } from '@/components/ui/phone-input';
import { Input } from '@heidi/design-system/input';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { FieldType, ScreenName } from '@heidi/runtime/analytics-vocabulary';

import { trackFieldSelected } from '@/lib/analytics/scribe/ui';
import { personalizationSharedMessages } from './personalization-shared.messages';

interface RequestDemoPhoneAndReasonFieldsProps {
  phoneNumber: string;
  onPhoneNumberChange: (value: string) => void;
  defaultCountry?: Country;
  demoReason: string;
  onDemoReasonChange: (value: string) => void;
  /**
   * Disables both inputs (e.g. when the parent's opt-in checkbox is unchecked).
   * Also suppresses the invalid-phone error so users don't see a red error
   * tied to a field they haven't engaged with yet.
   */
  disabled?: boolean;
  /**
   * When true, the phone + reason fields are presented as optional: the red
   * required `*` markers are hidden. The inline invalid-phone error still shows
   * so a user who types a malformed number gets feedback. Defaults to false
   * (required), preserving the request-demo behaviour where HubSpot's SDR form
   * 400s without a valid phone + reason.
   */
  optional?: boolean;
  /** Country name passed through to the DEV-11979 reason_for_demo_phone field_selected event. */
  country?: string;
  /** Language code passed through to the DEV-11979 reason_for_demo_phone field_selected event. */
  language?: string;
}

/**
 * Phone-number + reason inputs shared by every request-demo opt-in surface
 * (LearnBasics `DemoOptInSection`, v2 `RequestDemoSection`, legacy modal).
 *
 * Extracted from f4c402759e so the markup, accessibility (WCAG 1.3.1
 * `aria-labelledby`), and inline `isValidPhoneNumber` validation live in
 * exactly one place — HubSpot's SDR form 400s without a valid phone + reason,
 * so both fields are marked required and validated locally.
 */
export function RequestDemoPhoneAndReasonFields({
  phoneNumber,
  onPhoneNumberChange,
  defaultCountry,
  demoReason,
  onDemoReasonChange,
  disabled = false,
  optional = false,
  country,
  language,
}: RequestDemoPhoneAndReasonFieldsProps) {
  const intl = useIntl();

  const showPhoneError = !disabled && !!phoneNumber && !isValidPhoneNumber(phoneNumber);

  // DEV-11979: debounced reason_for_demo_phone field_selected tracker.
  // Mirrors the 500ms debounce other text-field trackers use (first_name,
  // last_name, phone_number, etc.) so we don't fire per keystroke. `country`
  // and `language` are passed at call time — keeping them out of the deps
  // means a pending keystroke isn't dropped when those props change.
  const debouncedTrackReason = useMemo(
    () =>
      debounce((countryArg?: string, languageArg?: string) => {
        trackFieldSelected({
          fieldName: 'reason_for_demo_phone',
          fieldType: FieldType.Text,
          screenName: ScreenName.OnboardingUserSurvey,
          country: countryArg,
          language: languageArg,
        });
      }, 500),
    []
  );

  useEffect(() => () => debouncedTrackReason.cancel(), [debouncedTrackReason]);

  const handleDemoReasonChange = (value: string) => {
    onDemoReasonChange(value);
    debouncedTrackReason(country, language);
  };

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex w-full flex-col gap-1">
        <div className="flex gap-0.5">
          <TypographyP2Bold id="demo-phone-label" className="leading-tight">
            <FormattedMessage {...personalizationSharedMessages.phoneNumber} />
          </TypographyP2Bold>
          {!optional && <span className="text-validation-destructive">*</span>}
        </div>
        <PhoneInput
          id="demo-phone-input"
          value={phoneNumber}
          onChange={(value) => onPhoneNumberChange((value as string) ?? '')}
          defaultCountry={defaultCountry}
          placeholder={intl.formatMessage(
            personalizationSharedMessages.phoneNumberPlaceholder
          )}
          disabled={disabled}
          aria-labelledby="demo-phone-label"
        />
        {showPhoneError && (
          <div className="flex items-start gap-2 rounded-md bg-validation-destructive-muted p-2">
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-validation-destructive" />
            <TypographyCaption className="text-validation-destructive">
              <FormattedMessage {...personalizationSharedMessages.invalidPhoneNumber} />
            </TypographyCaption>
          </div>
        )}
      </div>
      <div className="flex w-full flex-col gap-1">
        <div className="flex gap-0.5">
          <TypographyP2Bold id="demo-reason-label" className="leading-tight">
            <FormattedMessage {...personalizationSharedMessages.demoReasonLabel} />
          </TypographyP2Bold>
          {!optional && <span className="text-validation-destructive">*</span>}
        </div>
        <Input
          id="demo-reason-input"
          value={demoReason}
          onChange={(e) => handleDemoReasonChange(e.target.value)}
          placeholder={intl.formatMessage(
            personalizationSharedMessages.demoReasonPlaceholder
          )}
          disabled={disabled}
          aria-labelledby="demo-reason-label"
        />
      </div>
    </div>
  );
}
